import { useCallback, useEffect, useState } from 'react';
import { aaiApi } from '../services/aaiApi';

/*
 * The skill run loop.
 *
 * One copy of the conversation with /my/skills/{id}/run-turn, so the runner
 * can be drawn in a modal, a drawer, or anywhere else without two places
 * disagreeing about how a run behaves.
 *
 * The server owns every rule here: it re-checks access on each turn, decides
 * which tools need approval, and reports whether a connection is live. This
 * hook holds no policy of its own — it sends the history plus the tools the
 * person has approved for this run, and reports back what came home. Approval
 * is per run and never persisted, which is why `approved` starts empty every
 * time a runner mounts.
 */

export interface RunMessage {
  role: 'user' | 'assistant';
  content: string;
}

export function useSkillRun(skillId: string) {
  const [messages, setMessages] = useState<RunMessage[]>([]);
  const [approved, setApproved] = useState<string[]>([]);
  const [needsApproval, setNeedsApproval] = useState<string[]>([]);
  const [gmailEnabled, setGmailEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const turn = useCallback(async (history: RunMessage[], approvedNow: string[]) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await aaiApi.runSkillTurn(skillId, history, approvedNow);
      setMessages([...history, { role: 'assistant', content: data.reply }]);
      setNeedsApproval(data.needs_approval || []);
      setGmailEnabled(!!data.gmail?.enabled);
    } catch {
      setError('The run hit a snag. Send that again.');
      // The failed turn is rolled back, so a retry does not send it twice.
      setMessages(history);
    } finally {
      setBusy(false);
    }
  }, [skillId]);

  useEffect(() => { void turn([], []); }, [turn]);

  const send = async (text: string) => {
    const trimmed = text.trim();
    if (!trimmed || busy) return;
    const history: RunMessage[] = [...messages, { role: 'user', content: trimmed }];
    setMessages(history);
    await turn(history, approved);
  };

  const approveTool = async (tool: string) => {
    const next = [...approved, tool];
    setApproved(next);
    setNeedsApproval((pending) => pending.filter((t) => t !== tool));
    await turn(messages, next);
  };

  return { messages, needsApproval, gmailEnabled, busy, error, send, approveTool };
}
